// src/components/common/LanguageSwitcher.js
import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGlobe, FaChevronDown } from 'react-icons/fa';
import { useTranslation } from '../../hooks/useTranslation';

const LanguageSwitcherContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const LanguageButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.95);
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  flex-shrink: 0;
  white-space: nowrap;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: scale(1.05);
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
  
  svg {
    font-size: 1rem;
    flex-shrink: 0;
  }
  
  /* Tablet */
  @media (max-width: 1024px) {
    padding: 0.45rem 0.85rem;
    font-size: 0.85rem;
    gap: 0.4rem;
    
    svg {
      font-size: 0.9rem;
    }
  }
  
  /* Mobile - show only flag and icon */
  @media (max-width: 768px) {
    padding: 0.4rem 0.6rem;
    gap: 0.3rem;
    
    span {
      font-size: 0.8rem;
    }
    
    svg {
      font-size: 0.85rem;
    }
  }
  
  /* Very small mobile - hide language name text */
  @media (max-width: 500px) {
    padding: 0.4rem;
    
    span {
      display: none;
    }
  }
`;

const LanguageDropdown = styled(motion.div)`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background: rgba(15, 15, 15, 0.98);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  min-width: 150px;
  overflow: hidden;
  z-index: 10001;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const LanguageOption = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  text-align: left;
  color: var(--text-primary-dark);
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: var(--primary-color);
  }
  
  &.active {
    background: var(--primary-rgba-15);
    color: var(--primary-color);
    font-weight: 600;
  }
  
  .flag {
    font-size: 1.2rem;
  }
`;

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
];

const dropdownVariants = {
  hidden: { 
    opacity: 0, 
    y: -10,
    transition: {
      duration: 0.2
    }
  },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.3
    }
  }
};

const LanguageSwitcher = () => {
  const { language, changeLanguage } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const currentLanguage = languages.find(lang => lang.code === language) || languages[0];

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setIsOpen(false);
  };

  return (
    <LanguageSwitcherContainer ref={containerRef}>
      <LanguageButton
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <FaGlobe />
        <span>{currentLanguage.flag} {currentLanguage.name}</span>
        <FaChevronDown style={{ fontSize: '0.75rem' }} />
      </LanguageButton>
      
      <AnimatePresence>
        {isOpen && (
          <LanguageDropdown
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={dropdownVariants}
          >
            {languages.map((lang) => (
              <LanguageOption
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={language === lang.code ? 'active' : ''}
                whileHover={{ x: 4 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="flag">{lang.flag}</span>
                <span>{lang.name}</span>
              </LanguageOption>
            ))}
          </LanguageDropdown>
        )}
      </AnimatePresence>
    </LanguageSwitcherContainer>
  );
};

export default LanguageSwitcher;




